import React from 'react';
import { Motion, spring } from 'react-motion';
import { Chip, ChipsWrapper, ChipsInnerWrapper } from './CommonCarousel';
import { CarouselController } from './CarouselController';
import { doctrineAnalytics } from '../../../utils/analyticsUtils';

type Props = {
  children: (resetScroll: Function) => React.ReactNode;
};

type State = {
  displayLeftButton: boolean;
  displayRightButton: boolean;
  scrollLeft: number;
};

export class MouseCarousel extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.internalState = {
      cachedScrollLeft: 0,
      scrollingStart: 0,
      isClick: false,
      clientX: 0,
      isScrolling: false,
    };
  }

  state: State = {
    displayLeftButton: false,
    displayRightButton: false,
    scrollLeft: 0,
  };

  componentDidMount() {
    this.setState({ ...this.getButtonsState(0) });
    window.addEventListener('mousemove', this.onMouseMove);
    window.addEventListener('mouseup', this.unTrackMouseMove);
  }

  componentWillUnmount() {
    window.removeEventListener('mousemove', this.onMouseMove);
    window.removeEventListener('mouseup', this.unTrackMouseMove);
  }

  onMouseMove = (event: MouseEvent) => {
    if (this.swipPanel && this.internalState.isScrolling) {
      const { clientX, cachedScrollLeft } = this.internalState;
      const max = this.swipPanel.scrollWidth;
      const value = Math.max(
        0,
        Math.min(cachedScrollLeft + clientX - event.clientX, max),
      );
      this.setState({
        scrollLeft: value,
        ...this.getButtonsState(value),
      });
    }
  };

  onMouseUp = () => {
    const scrollingEnd = +new Date();
    const isScrolling = scrollingEnd - this.internalState.scrollingStart > 200;
    this.internalState = {
      ...this.internalState,
      isClick: !isScrolling,
      isScrolling: false,
      clientX: 0,
      scrollingStart: 0,
    };
  };

  onMouseDown = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (this.swipPanel) {
      const { scrollLeft } = this.swipPanel;
      this.internalState = {
        ...this.internalState,
        scrollingStart: +new Date(),
        isScrolling: true,
        cachedScrollLeft: scrollLeft,
        clientX: event.clientX,
      };
    }
  };

  onScroll = (value: { [key: string]: number }) => {
    if (this.swipPanel && 'x' in value) {
      this.swipPanel.scrollLeft = value.x;
    }
    return null;
  };

  getButtonsState(value: number) {
    const { width } = this.swipPanel
      ? this.swipPanel.getBoundingClientRect()
      : { width: 0 };
    const max = this.swipPanel ? this.swipPanel.scrollWidth : 0;

    return {
      displayLeftButton: value > 0,
      displayRightButton: value < max - width,
    };
  }

  unTrackMouseMove = () => {
    this.internalState.isScrolling = false;
  };

  toggleScrolling = (isEnable: boolean) => {
    if (isEnable) {
      window.addEventListener('mousemove', this.onMouseMove);
    } else {
      window.removeEventListener('mousemove', this.onMouseMove);
    }
  };

  goRight = () => {
    const { width } = this.swipPanel
      ? this.swipPanel.getBoundingClientRect()
      : { width: 0 };
    const newScrollLeft = this.swipPanel
      ? this.swipPanel.scrollLeft +
        Math.min(width / 2, this.swipPanel.scrollWidth - width)
      : 0;
    this.setState({
      scrollLeft: newScrollLeft,
      ...this.getButtonsState(newScrollLeft),
    });
    doctrineAnalytics('Search KeywordScrolled', {
      type: 'clicked',
      direction: 'right',
    });
  };

  goLeft = () => {
    const { width } = this.swipPanel
      ? this.swipPanel.getBoundingClientRect()
      : { width: 0 };
    const newScrollLeft = this.swipPanel
      ? this.swipPanel.scrollLeft -
        Math.min(width / 2, this.swipPanel.scrollWidth - width)
      : 0;
    this.setState({
      scrollLeft: newScrollLeft,
      ...this.getButtonsState(newScrollLeft),
    });
    doctrineAnalytics('Search KeywordScrolled', {
      type: 'clicked',
      direction: 'left',
    });
  };

  resetScroll = () => {
    this.setState({ scrollLeft: 0, ...this.getButtonsState(0) });
  };

  preventBubblingPhaseOnScroll = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!this.internalState.isClick) {
      event.stopPropagation();
    }
  };

  attachRef = (scroller: HTMLDivElement) => {
    this.swipPanel = scroller;
  };

  swipPanel?: HTMLDivElement;

  internalState: {
    cachedScrollLeft: number;
    scrollingStart: number;
    isClick: boolean;
    isScrolling: boolean;
    clientX: number;
  };

  render() {
    return (
      <Chip>
        {this.state.displayLeftButton && (
          <CarouselController direction="left" onClick={this.goLeft} />
        )}
        <ChipsWrapper
          ref={this.attachRef}
          onMouseDown={this.onMouseDown}
          onMouseUp={this.onMouseUp}
          onClickCapture={this.preventBubblingPhaseOnScroll}
        >
          <Motion style={{ x: spring(this.state.scrollLeft) }}>
            {val => {
              this.onScroll(val);
              return (
                <ChipsInnerWrapper>
                  <>{this.props.children(this.resetScroll)}</>
                </ChipsInnerWrapper>
              );
            }}
          </Motion>
        </ChipsWrapper>
        {this.state.displayRightButton && (
          <CarouselController direction="right" onClick={this.goRight} />
        )}
      </Chip>
    );
  }
}
